import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cs from "classnames";
import styles from "./styles.module.scss";
import { ChevronDownIcon } from "../NewOrderSelectApp/icons";
import { SelectProps } from "./types";

// Menu height cap (keep in sync with .menu max-height) — used to decide
// whether the list still fits under the trigger.
const MENU_MAX_HEIGHT = 280;
const MENU_GAP = 4;

/**
 * DS single select: a control-styled trigger and an option list portaled to
 * <body> and pinned to the trigger, so a modal or a card can't clip it.
 */
export const Select: React.FC<SelectProps> = ({
  value,
  options,
  onChange,
  placeholder = "",
  className,
  "aria-label": ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<React.CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const selected = options.find((o) => o.value === value);

  const placeMenu = useCallback(() => {
    const trigger = rootRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const spaceBelow = window.innerHeight - trigger.bottom;
    const openUp =
      spaceBelow < MENU_MAX_HEIGHT + MENU_GAP && trigger.top > spaceBelow;
    setMenuBox({
      left: trigger.left,
      width: trigger.width,
      ...(openUp
        ? {
            bottom: window.innerHeight - trigger.top + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, trigger.top - MENU_GAP * 2),
          }
        : {
            top: trigger.bottom + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, spaceBelow - MENU_GAP * 2),
          }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    placeMenu();
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    // A scroll anywhere moves the trigger — follow it. The menu's own list
    // scroll doesn't (and the capture listener sees it too), so skip it.
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      placeMenu();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", placeMenu);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", placeMenu);
    };
  }, [open, placeMenu]);

  // Bring the current value into view once the menu is first placed. Not on
  // every reposition, or the list snaps back to the value while scrolling.
  const placed = menuBox !== null;
  useEffect(() => {
    if (!open || !placed) return;
    menuRef.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [open, placed]);

  return (
    <div ref={rootRef} className={className}>
      <button
        type="button"
        className={cs(styles.trigger, { [styles.triggerOpen]: open })}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={cs(styles.value, { [styles.placeholder]: !selected })}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDownIcon
          className={cs(styles.chevron, { [styles.chevronOpen]: open })}
        />
      </button>
      {open &&
        menuBox &&
        createPortal(
          <ul
            ref={menuRef}
            className={styles.menu}
            style={menuBox}
            role="listbox"
          >
            {options.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  className={cs(styles.option, {
                    [styles.optionActive]: o.value === value,
                  })}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  {o.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default Select;
